/**
 * DemandGenAdsPanel Component
 * Displays Demand Gen ads (Multi-Asset, Video, Carousel) in an ad group
 *
 * Similar to RDAPanel, provides read-only display with edit capability
 */

import React from 'react';
import { PhotoIcon, VideoCameraIcon, RectangleStackIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import type { AdGroup, Ad, Campaign } from '@/types/campaign';
import type { DemandGenCacheEntry } from '@/hooks/useDemandGenCache';
import { getDemandGenAdsFromAdGroup } from '../utils';

interface DemandGenAdsPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;
  onCreateAd?: () => void;
  onEditAd?: (adId: string) => void;
}

// Helper to determine ad type from ad_type string
function getDemandGenAdTypeInfo(adType: string): {
  type: 'MULTI_ASSET' | 'VIDEO' | 'CAROUSEL' | 'UNKNOWN';
  label: string;
  icon: React.ReactNode;
} {
  if (adType.includes('MULTI_ASSET')) {
    return {
      type: 'MULTI_ASSET',
      label: 'Multi-Asset',
      icon: <PhotoIcon className="w-4 h-4" />,
    };
  }
  if (adType.includes('VIDEO')) {
    return {
      type: 'VIDEO',
      label: 'Video',
      icon: <VideoCameraIcon className="w-4 h-4" />,
    };
  }
  if (adType.includes('CAROUSEL')) {
    return {
      type: 'CAROUSEL',
      label: 'Carousel',
      icon: <RectangleStackIcon className="w-4 h-4" />,
    };
  }
  return {
    type: 'UNKNOWN',
    label: 'Demand Gen',
    icon: <PhotoIcon className="w-4 h-4" />,
  };
}

// Get display text for a Demand Gen ad
// Format: "<ad_name> | <headline>" or just "<ad_name>" or "<headline>" if one is missing
// Uses cached data when available (more up-to-date than ad object from app_configuration)
// Waterfall: ad_name | headline -> ad_name -> headline -> business_name -> Ad ID
function getDisplayText(ad: Ad, cached?: DemandGenCacheEntry): string {
  // Prefer cached ad_name, fall back to ad data
  const adName = cached?.ad_name || ad.ad_name || '';
  let headline = '';

  // Prefer cached headlines, fall back to ad data
  const headlines = cached?.headlines || ad.headlines;
  if (headlines && headlines.length > 0) {
    const firstHeadline = headlines[0];
    headline = typeof firstHeadline === 'string' ? firstHeadline : (firstHeadline as { text: string }).text;
  }

  // Both ad_name and headline available
  if (adName && headline) {
    return `${adName} | ${headline}`;
  }

  // Only ad_name
  if (adName) {
    return adName;
  }

  // Only headline
  if (headline) {
    return headline;
  }

  // Fall back to business_name (from cache or ad object)
  const businessName = cached?.business_name || ad.business_name;
  if (businessName) {
    return businessName;
  }

  // Final fallback to ad ID
  return `Demand Gen Ad ${ad.ad_id.slice(-6)}`;
}

// Get summary info for a Demand Gen ad (uses cache if available)
function getAdSummary(ad: Ad, cached?: DemandGenCacheEntry): string {
  const parts: string[] = [];

  // Use cached data if available, otherwise fall back to ad data
  const headlineCount = cached?.headlines?.length ?? ad.headlines?.length ?? 0;
  const descCount = cached?.descriptions?.length ?? ad.descriptions?.length ?? 0;

  if (headlineCount > 0) {
    parts.push(`${headlineCount} headline${headlineCount !== 1 ? 's' : ''}`);
  }

  if (descCount > 0) {
    parts.push(`${descCount} description${descCount !== 1 ? 's' : ''}`);
  }

  // Images count from cache (more accurate)
  if (cached) {
    const imageCount =
      (cached.marketing_images?.length || 0) +
      (cached.square_marketing_images?.length || 0) +
      (cached.portrait_marketing_images?.length || 0) +
      (cached.logo_images?.length || 0);
    if (imageCount > 0) {
      parts.push(`${imageCount} image${imageCount !== 1 ? 's' : ''}`);
    }

    // Videos count for Video ads
    const videoCount = cached.video_ids?.length || 0;
    if (videoCount > 0) {
      parts.push(`${videoCount} video${videoCount !== 1 ? 's' : ''}`);
    }

    // Carousel cards for Carousel ads
    const cardCount = cached.carousel_cards?.length || 0;
    if (cardCount > 0) {
      parts.push(`${cardCount} card${cardCount !== 1 ? 's' : ''}`);
    }
  } else {
    // Fallback to ad data estimates
    const imageCount =
      ((ad as unknown as { landscape_image_count?: number }).landscape_image_count || 0) +
      ((ad as unknown as { square_image_count?: number }).square_image_count || 0) +
      ((ad as unknown as { logo_count?: number }).logo_count || 0);
    if (imageCount > 0) {
      parts.push(`${imageCount} image${imageCount !== 1 ? 's' : ''}`);
    }
  }

  return parts.length > 0 ? parts.join(' | ') : 'View details';
}

export function DemandGenAdsPanel({
  adGroup,
  demandGenCache,
  isLoadingDemandGenAds,
  onCreateAd,
  onEditAd,
}: DemandGenAdsPanelProps) {
  const demandGenAds = getDemandGenAdsFromAdGroup(adGroup);

  // Show loading state while fetching cache
  if (isLoadingDemandGenAds) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (demandGenAds.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <PhotoIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">No Demand Gen Ads</p>
          <p className="text-sm text-gray-500 mt-1">
            Create your first Demand Gen ad to reach customers across YouTube, Discover, Gmail, and more.
          </p>
          {onCreateAd && (
            <Button
              variant="primary"
              onClick={onCreateAd}
              className="mt-4"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Create Demand Gen Ad
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          {demandGenAds.length} ad{demandGenAds.length !== 1 ? 's' : ''} in this ad group
        </p>
        {onCreateAd && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onCreateAd}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Create New Ad
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {demandGenAds.map((ad) => {
          const cached = demandGenCache[ad.ad_id];
          // Use cached ad_type if available, otherwise fall back to ad data
          const adType = cached?.ad_type ? `DEMAND_GEN_${cached.ad_type}_AD` : (ad.ad_type || '');
          const typeInfo = getDemandGenAdTypeInfo(adType);
          const isPaused = ad.status === 'PAUSED';
          const isRemoved = ad.status === 'REMOVED';
          const canEdit = onEditAd && !isRemoved;

          return (
            <div
              key={ad.ad_id}
              role={canEdit ? 'button' : undefined}
              tabIndex={canEdit ? 0 : undefined}
              onClick={canEdit ? () => onEditAd(ad.ad_id) : undefined}
              onKeyDown={canEdit ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEditAd(ad.ad_id);
                }
              } : undefined}
              className={`p-4 border rounded-lg transition-colors ${
                isRemoved
                  ? 'bg-red-50 border-red-200'
                  : isPaused
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-white border-gray-200'
              } ${canEdit ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2' : ''}`}
            >
              {/* Ad Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${
                    isRemoved
                      ? 'bg-red-100 text-red-700'
                      : isPaused
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-blue-100 text-blue-700'
                  }`}>
                    {typeInfo.icon}
                    {typeInfo.label}
                  </span>
                  {isPaused && (
                    <span className="text-xs text-yellow-600">(Paused)</span>
                  )}
                  {isRemoved && (
                    <span className="text-xs text-red-600">(Removed)</span>
                  )}
                </div>
                {canEdit && (
                  <PencilIcon className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                )}
              </div>

              {/* Ad Content */}
              <div>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getDisplayText(ad, cached)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {getAdSummary(ad, cached)}
                </p>
              </div>

              {/* Final URL */}
              {ad.final_urls && ad.final_urls.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 truncate">
                    <span className="font-medium">URL:</span>{' '}
                    {typeof ad.final_urls[0] === 'string'
                      ? ad.final_urls[0]
                      : (ad.final_urls[0] as { url: string }).url}
                  </p>
                </div>
              )}

              {/* Ad ID for reference */}
              <p className="text-xs text-gray-400 mt-2">
                ID: {ad.ad_id}
              </p>
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Tip:</strong> Demand Gen ads appear across YouTube, Discover, Gmail, and more.
          Add multiple ad variations to improve performance through A/B testing.
        </p>
      </div>
    </div>
  );
}

export default DemandGenAdsPanel;
